import Resource, { createResourceHook } from '@tabeo/resync'
import api from '@tabeo/sharpei/utils/api'
import getFingerprint from '@tabeo/sharpei/utils/getFingerprint'
import { getJWT, parseJWT } from '@tabeo/sharpei/utils/jwt'
import refreshAccessToken from 'utils/refreshAccessToken'

const initialState = {
  token: null,
  user: null,
  userId: null,
}

class Auth extends Resource {
  getInitialConfig() {
    return {
      refreshInterval: 5 * 60 * 1000,
    }
  }

  constructor(params) {
    const initialToken = getJWT('token') || getJWT('refreshToken')
    const userId = initialToken ? Number(parseJWT(initialToken).id) : null

    const data = {
      ...initialState,
      token: initialToken,
      userId,
    }
    super(params, {}, data)

    api.interceptors.response.use(null, async error => {
      const originalRequest = error.config
      if (
        error.response.status === 401 &&
        !originalRequest.url.startsWith('/refresh') &&
        !originalRequest.url.startsWith('/login')
      ) {
        if (!originalRequest.didRetry) {
          originalRequest.didRetry = true
          try {
            await this.refreshAccessToken()
          } catch {
            // The refresh failed but the session goes on: reject with the
            // original error, so callers still get its response.
            return Promise.reject(error)
          }
          return api(originalRequest)
        }
      }
      return Promise.reject(error)
    })
  }

  refreshAccessToken = async () => {
    try {
      const { token } = await refreshAccessToken()

      this.data = {
        ...this.data,
        token,
      }
    } catch (error) {
      // A dropped connection or a server error keeps the session; only a
      // rejected or missing refresh token ends it.
      if (!error?.isExpectedSessionExpiry) throw error

      this.logout({ clientSide: true })
    }
  }

  async fetch() {
    if (this.data.token) {
      const {
        user: { id },
      } = await api.get(`/user`)
      const { admin } = await api.get(`/admins/${id}?subResource=user`)
      const user = {
        ...admin,
        ...admin.user,
      }
      this.data = {
        ...this.data,
        user,
      }
    }
  }

  login = async credentials => {
    const { token, refresh } = await api.post('/login', {
      ...credentials,
      fingerprint: getFingerprint(),
    })
    const {
      user: { roles },
    } = await api.get(`/user`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (!roles.includes('admin')) {
      const error = new Error('Unauthorized')
      error.type = 'role'
      throw error
    }
    window.localStorage.setItem('token', token)
    window.localStorage.setItem('refreshToken', refresh)

    const userId = Number(parseJWT(token).id)

    this.data = {
      ...this.data,
      token,
      userId,
    }
  }

  logout = async ({ redirect = '/login', clientSide } = {}) => {
    if (!clientSide) {
      await api.post('/logout', {
        fingerprint: getFingerprint(),
      })
    }
    window.localStorage.removeItem('refreshToken')
    window.localStorage.removeItem('token')
    window.location.href = redirect
  }

  changePassword = async payload => {
    await api.put('/user/password', payload)
    this.data = initialState
    window.localStorage.removeItem('refreshToken')
    window.localStorage.removeItem('token')
  }
}

export default Auth
export const useAuth = createResourceHook(Auth)
